type ActivityRoom = {
  id: string;
  created_at?: string | null;
  last_message_at?: string | null;
};

type RoomMembership = {
  room_id: string;
  joined_at?: string | null;
  last_read_at?: string | null;
};

type RoomMessage = {
  room_id?: string | null;
  created_at?: string | null;
  user_id?: string | null;
  is_deleted?: boolean | null;
};

export type ChatRoomActivityState = {
  is_member: boolean;
  joined_at: string | null;
  last_read_at: string | null;
  last_activity_at: string | null;
  unread_count: number;
};

const toTimestamp = (value?: string | null) => {
  if (!value) {
    return 0;
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

export const applyChatRoomActivityState = <
  TRoom extends ActivityRoom,
  TMembership extends RoomMembership,
  TMessage extends RoomMessage,
>(
  rooms: TRoom[],
  memberships: TMembership[],
  messages: TMessage[],
  currentUserId?: string | null,
): Array<TRoom & ChatRoomActivityState> => {
  const membershipByRoom = new Map(
    memberships
      .filter((membership) => membership.room_id)
      .map((membership) => [membership.room_id, membership]),
  );
  const latestActivityByRoom = new Map<string, number>();
  const unreadCountByRoom = new Map<string, number>();

  rooms.forEach((room) => {
    latestActivityByRoom.set(
      room.id,
      Math.max(toTimestamp(room.created_at), toTimestamp(room.last_message_at)),
    );
  });

  messages.forEach((message) => {
    if (!message.room_id || !message.created_at || message.is_deleted) {
      return;
    }

    const timestamp = toTimestamp(message.created_at);
    latestActivityByRoom.set(
      message.room_id,
      Math.max(latestActivityByRoom.get(message.room_id) ?? 0, timestamp),
    );

    if (!currentUserId || message.user_id === currentUserId) {
      return;
    }

    const membership = membershipByRoom.get(message.room_id);
    if (!membership) {
      return;
    }

    const cutoff = Math.max(
      toTimestamp(membership.last_read_at),
      toTimestamp(membership.joined_at),
    );

    if (timestamp > cutoff) {
      unreadCountByRoom.set(message.room_id, (unreadCountByRoom.get(message.room_id) ?? 0) + 1);
    }
  });

  return [...rooms]
    .sort((left, right) => (latestActivityByRoom.get(right.id) ?? 0) - (latestActivityByRoom.get(left.id) ?? 0))
    .map((room) => {
      const membership = membershipByRoom.get(room.id);
      const lastActivityAt = latestActivityByRoom.get(room.id) ?? 0;

      return {
        ...room,
        is_member: Boolean(membership),
        joined_at: membership?.joined_at ?? null,
        last_read_at: membership?.last_read_at ?? null,
        last_activity_at: lastActivityAt ? new Date(lastActivityAt).toISOString() : null,
        unread_count: unreadCountByRoom.get(room.id) ?? 0,
      };
    });
};
